import type { SnEvent } from './types'

/**
 * Who owns an event — a competition, or a team.
 *
 * The discriminator is `schedule`, NOT `team`.
 *
 * A competition event may now carry a `team` of its own (the Team select on the
 * add/edit form assigns one), so `team` no longer separates the two kinds. What
 * still does is `schedule`: a competition event names the schedule it sits on,
 * and a team-owned event has none.
 *
 * `root_schedule` is not a substitute — a TEAM event still reports the
 * organization's `root_schedule`, so keying off that alone would treat every
 * team event as a competition one.
 */
export const isCompetitionEvent = (event?: SnEvent | null): boolean =>
  Boolean(event?.schedule)

/**
 * The competition to scope the form's option lists to (`/venues`,
 * `/schedules/:id/teams/simple`), or undefined for a team-owned event.
 *
 * `root_schedule` — the TOP-LEVEL competition — is preferred over `schedule`,
 * which may be a division: both endpoints are scoped to the competition owning
 * the pool, and a division id returns the wrong one (see
 * `web/src/features/game/gameListView/utils/editModalScheduleId.ts`).
 * `schedule` is the fallback for responses that omit the root.
 */
export const resolveEventScheduleId = (
  event?: SnEvent | null
): string | undefined => {
  if (!isCompetitionEvent(event)) {
    return undefined
  }
  return event?.root_schedule?.id ?? event?.schedule?.id ?? undefined
}

interface ResolveEventFormScheduleIdArgs {
  /** The event being EDITED. Null/undefined means the form is in add mode. */
  event?: SnEvent | null
  /**
   * The surface's own competition scope, used only for ADD — the competition
   * page's `SCHEDULE_ID`, or nothing on a team page or the detail page.
   */
  hostScheduleId?: string | null
}

/**
 * The competition scope the add/edit event form should run under.
 *
 * **On EDIT the answer comes from the EVENT, never from the host surface.**
 * That is the whole point: a competition list and a team list can each hold
 * both kinds of event — the two payloads differ only in `schedule`, sharing a
 * `team` AND a `root_schedule` — so the page an event was opened from says
 * nothing about which kind it is.
 *
 * Taking the host's scope instead is not merely cosmetic. It feeds
 * `buildEventPayload`'s `hasTeamField`, so editing a TEAM-owned event from a
 * competition page would show the Team select and let a save send
 * `team_id: null`, unlinking the event from the team it was created under. The
 * reverse — a competition event edited from a team page — hides the Location
 * and Team pickers and offers an address block for a venue-backed event.
 *
 * On ADD there is no event to ask, so the host's own scope is the answer: the
 * competition page creates schedule-owned events, a team page creates
 * team-owned ones (and passes none).
 *
 * The games side learned this the same way — see
 * `web/src/features/game/gameListView/utils/editModalScheduleId.ts`, which
 * resolves the mirror-image problem for the Game modal.
 */
export const resolveEventFormScheduleId = ({
  event,
  hostScheduleId,
}: ResolveEventFormScheduleIdArgs): string | undefined =>
  event ? resolveEventScheduleId(event) : hostScheduleId || undefined
